/**
 * 외부 API — 관리 콘솔 (2026-09-21 · 시안 「외부 API 콘솔」 확정).
 *
 * ## ★★★ 이 화면이 없으면 날씨가 «켜지지 않는다»
 *
 * 서버(#1525)는 다 서 있는데 **키를 넣을 자리가 없어** 운영에서 DB 에 손으로 넣어야 했다. 이 화면이 그 스위치다.
 *
 * ## 규격
 *
 * 콘솔 공통 목록 규격(2026-09-09) — 머리 → 툴바(검색·거르개·등록) → 본문. 줄 끝 단추, 줄을 누르면 팝업.
 * 기준으로 삼은 화면은 **앱 배포**(`AppReleaseSection`)다. 8444 는 디자인 정본 범위 밖이라 **새 언어를 만들지 않았다.**
 *
 * ## 키를 다루는 규칙
 *
 *   · 넣을 때만 지나간다 — 응답에는 **끝 네 자리**(`key_hint`)뿐이고, 옛 키를 보여 주는 길이 없다.
 *   · 바꾸는 길은 **새 키로 덮어쓰기**뿐. 마스킹된 값을 되돌려 보내면 서버가 거절한다(`MASKED_VALUE`).
 *   · **저장 전에 시험**할 수 있다(`POST /admin/external-apis/test` — 저장하지 않고 판정만).
 *     ★ 새 경로를 만들지 않았다 — 이미 있던 길이다. 키가 지나가는 경로를 늘리지 않으려는 것.
 *
 * ## 한도는 종류마다 따로다
 *
 * 기상청은 **오퍼레이션마다 10,000**(실황·초단기·단기). 합쳐 세면 3배 일찍 멈춘다 — 그래서 줄 안에 **종류별로** 그린다.
 * 공급자가 헤더로 남은 수를 주면 그 값이 정본이고, 안 주는 공급자(도로명주소)는 **우리가 센 수**만 보인다.
 *
 * ## 한 탭 안에 보기가 둘이다 — 「공급자」와 「날씨 위치」 (2026-09-21 · 시안 v2)
 *
 * 탭을 새로 늘리지 않고 좌 분류(형 B)로 갈랐다. **둘은 같은 질문의 앞뒤**이기 때문이다 —
 * 「키를 넣었나」(공급자)와 **「그래서 어느 곳이 날씨가 나오나」**(날씨 위치). 키가 멀쩡해도
 * 주소가 시·도까지만 적힌 곳은 날씨를 내지 않으므로, 둘을 떼어 놓으면 관리자가 절반만 보고 끝낸다.
 *
 * ## 셋째 칸 — 「STT」 (2026-09-21)
 *
 * 음성 인식도 **같은 표**에 산다(`category='stt'`). 그래서 탭을 또 늘리지 않고 **좌 분류에 한 칸**을 더했다.
 * ⚠ 다만 **표는 따로** 그린다 — 칸이 달라(끝점 · 모델 · 타임아웃) 한 표에 섞으면 「오늘 사용」·「한도」가
 * STT 줄에서 늘 빈칸이 되고, 늘 비어 있는 칸은 사람이 안 읽게 된다.
 * ⛔ 그래서 **공급자 보기에서는 STT 줄을 뺀다** — 한 줄이 두 표에 나오면 어느 쪽에서 고쳐야 하는지 흐려진다.
 *
 * ## 넷째 칸 — 「음성 저장」 (2026-09-21)
 *
 * STT 와 한 표에 두지 않는다. 엔진 줄은 **엔진마다 하나**이고 사용량 줄은 **회사마다 하나**라,
 * 한 표에 섞으면 무엇이 무엇의 줄인지 흐려진다. 같은 까닭으로 날씨와 STT 도 갈라 놓았다.
 *
 * ## 다섯째 칸 — 「화자 구분」 (2026-10-22)
 *
 * STT 와 같은 꼴(`DiarizePanel` · `DiarizeEditModal`). 같은 까닭으로 **공급자 표와 공급자 등록에서 diarize 를 뺀다.**
 * 칸은 서버가 준 어댑터 목록에 diarize 가 있을 때 연다 — 어댑터 없이 줄만 남은 판(되돌린 서버 등)에서는
 * 줄이 어느 표에도 안 보이게 되므로 그때도 칸은 두되 고치기를 열지 않는다.
 *
 * ## 여섯째 칸 — 「용어집」 (2026-10-23 · 시안 G 2판 · 사용자 답 「안 A — 메뉴 칸 · 표」)
 *
 * 받아 적기(STT) 힌트에 들어가는 회사 용어 — 엔진 줄이 아니라 «기관마다 용어 줄»이라 제 칸에 둔다(`GlossaryPanel`).
 * 툴바의 「용어 추가」 · 「여러 개 넣기」는 패널에 «부탁»만 넘기고 팝업은 패널이 연다(기관 · 표를 패널이 쥐고 있다).
 * ⛔ 칸을 열기 전에는 부르지 않는다 — 숨은 보기가 서버를 부르면 다른 보기의 시험이 진짜 네트워크를 탄다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, AlertTriangle } from '../../../icons';
import {
  listExternalApis, testProvider,
  type ExternalApiAdapter, type ExternalApiCategory, type ExternalApiProvider, type ProbeResult, type UsageByKind,
} from '../../../api/externalApis';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';
import { ExternalApiEditModal, errText } from './ExternalApiEditModal';
import { WeatherGridPanel } from './WeatherGridPanel';
import { SttPanel, sttStateOf } from './SttPanel';
import { SttEditModal } from './SttEditModal';
import { VoiceStoragePanel } from './VoiceStoragePanel';
import { DiarizePanel, diarizeFailed } from './DiarizePanel';
import { DiarizeEditModal } from './DiarizeEditModal';
import { GlossaryPanel } from './GlossaryPanel';
import './ExternalApi.css';

/**
 * 범주 이름 — 공급자 표의 둘째 줄에 쓴다.
 *
 * ⚠ `stt` 는 **오늘 이 표에 안 선다**(STT 줄은 제 보기로 갈라 뒀다). 그래도 칸을 비우지 않는다 —
 * 언젠가 한 줄이라도 새면 화면에 **`undefined · stt_openai_compatible`** 이 그대로 찍힌다.
 * ★ 낱말은 **서버가 쓰는 말**(`받아쓰기 (OpenAI 호환)`)에 맞춘다 — 「날씨 · 주소」와 같은 결의
 *   우리말 이름이라야 한 줄 안에서 어색하지 않다(좌 분류의 「STT」는 «보기 이름»이라 다른 자리다).
 */
const CATEGORY_LABEL: Record<ExternalApiCategory, string> = { weather: '날씨', address: '주소', stt: '받아쓰기', diarize: '화자 구분' };
/** 기상청 오퍼레이션 — 한도가 각각이다. 그 밖의 종류는 코드를 그대로 보여 준다. */
const KIND_LABEL: Record<string, string> = { ncst: '실황', vsrt: '초단기', shrt: '단기', '': '전체' };

const VERDICT_LABEL: Record<string, string> = {
  ok: '정상',
  auth_failed: '키 거절',
  shared_quota_exhausted: '한도 소진',
  provider_error: '공급자 오류',
  unreachable: '닿지 못함',
};
const VERDICT_CLASS: Record<string, string> = {
  ok: 'xa-badge--ok',
  auth_failed: 'xa-badge--bad',
  shared_quota_exhausted: 'xa-badge--warn',
  provider_error: 'xa-badge--bad',
  unreachable: 'xa-badge--bad',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** 남은 수가 있으면 그것이 정본 — 없으면 우리가 센 수만 적는다(한도 칸을 지어내지 않는다). */
function UsageRow({ row, quota }: { row: UsageByKind; quota: number | null }) {
  const limit = row.limit_seen ?? quota;
  const used = row.remaining_seen !== null && row.limit_seen !== null
    ? row.limit_seen - row.remaining_seen
    : row.calls;
  const ratio = limit && limit > 0 ? Math.min(1, used / limit) : 0;
  const tone = ratio >= 1 ? ' is-bad' : ratio >= 0.9 ? ' is-warn' : '';
  return (
    <span className="xa-usage__row">
      <span className="xa-usage__kind">{KIND_LABEL[row.kind] ?? row.kind}</span>
      <span className="xa-usage__bar"><i className={`xa-usage__fill${tone}`} style={{ width: `${Math.round(ratio * 100)}%` }} /></span>
      <span className="xa-usage__num">
        {used.toLocaleString()} / {limit ? limit.toLocaleString() : '한도 미입력'}
      </span>
    </span>
  );
}

function keyCell(p: ExternalApiProvider) {
  if (p.key_state === 'unreadable') return <span className="xa-badge xa-badge--bad">키를 못 읽음</span>;
  if (p.key_state === 'stored') {
    return (
      <span className="xa-key">
        <b>{p.key_hint}</b>
        <span>저장됨</span>
      </span>
    );
  }
  if (p.env_key_fallback) return <span className="xa-badge xa-badge--info">개발키로 돎</span>;
  return <span className="xa-badge xa-badge--muted">키 없음</span>;
}

export function ExternalApiSection() {
  const [list, setList] = useState<ExternalApiProvider[]>([]);
  const [adapters, setAdapters] = useState<ExternalApiAdapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | ExternalApiCategory | 'bad' | 'full'>('all');
  const [q, setQ] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<ExternalApiProvider | null>(null);
  const [adding, setAdding] = useState(false);
  /** 같은 탭 안의 세 보기 — 「공급자」 · 「날씨 위치」 · 「STT」(형 B). */
  const [view, setView] = useState<'providers' | 'weather-grid' | 'stt' | 'voice-storage' | 'diarize' | 'glossary'>('providers');
  /** 날씨 위치 쪽이 세어 알려 준 수 — 좌 분류 칸과 KPI 가 **같은 수**를 쓴다. */
  const [gridCount, setGridCount] = useState({ places: 0, bad: 0 });
  /** 음성 저장 쪽이 세어 알려 준 수 — 좌 분류 칸과 KPI 가 같은 수를 쓴다. */
  const [storageCount, setStorageCount] = useState({ companies: 0, full: 0 });
  const onStorageCount = useCallback((n: { companies: number; full: number }) => setStorageCount(n), []);
  const [editingStt, setEditingStt] = useState<ExternalApiProvider | null>(null);
  const [addingStt, setAddingStt] = useState(false);
  const [editingDiarize, setEditingDiarize] = useState<ExternalApiProvider | null>(null);
  const [addingDiarize, setAddingDiarize] = useState(false);
  const onGridCount = useCallback((n: { places: number; bad: number }) => setGridCount(n), []);
  /** ⭐ 2026-10-23 — 용어집 쪽이 세어 알려 준 수 · 툴바 단추가 넘긴 부탁(팝업은 패널이 연다). */
  const [glossaryCount, setGlossaryCount] = useState({ total: 0, inHint: 0 });
  const onGlossaryCount = useCallback((n: { total: number; inHint: number }) => setGlossaryCount(n), []);
  const [glossaryAsk, setGlossaryAsk] = useState<'add' | 'bulk' | null>(null);
  const onGlossaryAskDone = useCallback(() => setGlossaryAsk(null), []);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listExternalApis();
      setList(r.providers);
      setAdapters(r.adapters);
    } catch (e: unknown) {
      setError(errText(e, '외부 API 목록을 불러오지 못했습니다'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

/**
   * ★ STT 줄은 공급자 표에서 뺀다 — 표를 따로 그리기로 한 결정의 나머지 반쪽이다.
   *   (한 줄이 두 표에 나오면 어느 쪽에서 고치는지 흐려진다.)
   */
  const sttRows = useMemo(() => list.filter((p) => p.category === 'stt'), [list]);
  /** ⭐ 화자 구분 줄도 공급자 표에서 뺀다 — STT 와 같은 까닭(2026-10-22). */
  const diarizeRows = useMemo(() => list.filter((p) => p.category === 'diarize'), [list]);
  const apiRows = useMemo(() => list.filter((p) => p.category !== 'stt' && p.category !== 'diarize'), [list]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return apiRows.filter((p) => (filter === 'all' || p.category === filter)
      && (!needle || p.label.toLowerCase().includes(needle) || p.provider_key.includes(needle)));
  }, [apiRows, filter, q]);

  /** ★ 서버가 STT 를 부를 줄 아나 — 깃발이 아니라 서버가 준 어댑터 목록으로 정한다. */
  const sttReady = adapters.some((a) => a.category === 'stt');

  const shownStt = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return sttRows.filter((p) => !needle || p.label.toLowerCase().includes(needle) || p.provider_key.includes(needle));
  }, [sttRows, q]);

  /** ⭐ 서버가 화자 구분을 부를 줄 아나 — `sttReady` 와 같이 서버가 준 어댑터 목록으로 정한다. */
  const diarizeReady = adapters.some((a) => a.category === 'diarize');
  /** 좌 분류 칸을 둘 것인가 — 어댑터가 있거나, 어댑터 없이 줄이 남아 있을 때(그 줄이 어느 표에도 없게 되지 않게). */
  const diarizeShown = diarizeReady || diarizeRows.length > 0;

  const shownDiarize = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return diarizeRows.filter((p) => !needle || p.label.toLowerCase().includes(needle) || p.provider_key.includes(needle));
  }, [diarizeRows, q]);

  /** ★ 날씨가 실제로 도는가 — 켜져 있고 키를 읽을 수 있어야 한다. 아니면 대시보드·앱에 날씨가 없다. */
  const weatherLive = list.some((p) => p.category === 'weather' && p.enabled && (p.key_state === 'stored' || p.env_key_fallback));

  /** ★ 보기를 바꾸면 검색·거르개를 비운다 — 「공급자 이름」으로 찾던 글자가 「곳 이름」에 남으면 빈 표가 된다. */
  const changeView = useCallback((k: string) => {
    setView(
      k === 'glossary' ? 'glossary' : k === 'weather-grid' ? 'weather-grid'
        : k === 'stt' ? 'stt'
          : k === 'diarize' ? 'diarize'
          : k === 'voice-storage' ? 'voice-storage'
            : 'providers',
    );
    setQ(''); setFilter('all');
  }, []);

  const isGrid = view === 'weather-grid';
  const isStt = view === 'stt';
  const isStorage = view === 'voice-storage';
  const isDiarize = view === 'diarize';
  const isGlossary = view === 'glossary';

  async function runTest(p: ExternalApiProvider) {
    setBusyKey(p.provider_key); setNotice(null); setError(null);
    try {
      const r: ProbeResult = await testProvider(p.provider_key);
      setNotice(`${p.label} — ${VERDICT_LABEL[r.verdict] ?? r.verdict} · ${r.message}`);
      await reload();
    } catch (e: unknown) {
      setError(errText(e, '연결 시험에 실패했습니다'));
    } finally { setBusyKey(null); }
  }

  return (
    <>
      <ConsoleListScreen
        title="외부 API"
        kpi={isGlossary ? `용어 ${glossaryCount.total} · 힌트에 ${glossaryCount.inHint}` : isGrid
          ? `곳 ${gridCount.places}${gridCount.bad > 0 ? ` · 고칠 곳 ${gridCount.bad}` : ''}`
          : isDiarize
            ? `엔진 ${diarizeRows.length} · 켜짐 ${diarizeRows.filter((p) => p.enabled).length}${
              diarizeRows.some(diarizeFailed) ? ` · 연결 실패 ${diarizeRows.filter(diarizeFailed).length}` : ''}`
          : isStt
            ? `엔진 ${sttRows.length} · 동작 중 ${sttRows.filter((p) => sttStateOf(p) === 'live').length}`
            : isStorage
              ? `회사 ${storageCount.companies}${storageCount.full > 0 ? ` · 찬 곳 ${storageCount.full}` : ''}`
            : `공급자 ${apiRows.length} · 켜짐 ${apiRows.filter((p) => p.enabled).length}`}
        desc={isGlossary ? '받아 적기(STT)에 쓰는 회사 이름 · 제품 · 약어입니다 — 받아 적을 때 이 철자로 잡습니다. 위에서부터 힌트에 들어갑니다.' : isGrid
          ? '주소에서 그때그때 셈한 위치입니다 — 확정 단계가 없습니다. 주소를 고치면 다음 조회부터 따라갑니다.'
          : isDiarize
            ? '녹음이 끝난 뒤 「누가 말했나」를 가르는 엔진입니다 — 사내 받아쓰기 서버 옆의 서비스입니다.'
          : isStt
            ? '음성을 글로 바꾸는 엔진입니다. ⛔ 저장만으로는 「동작 중」이 되지 않습니다 — 연결 시험이 전사까지 되는지 봅니다.'
            : isStorage
              ? '회의 녹음 조각이 쓰는 자리입니다. 조각은 개인 용량에 안 잡혀서 여기가 유일하게 보이는 자리입니다.'
            : '날씨 · 주소 검색처럼 바깥 서비스를 부르는 자리입니다. 키 한 벌을 전 회사가 함께 씁니다.'}
        search={{
          value: q,
          onChange: setQ,
          placeholder: isGlossary ? '용어 · 메모로 찾기' : isGrid
            ? '회사 · 사업장 이름으로 찾기'
            : isDiarize ? '엔진 이름으로 찾기'
            : isStt ? '엔진 이름으로 찾기'
              : isStorage ? '회사 이름으로 찾기'
                : '공급자 이름으로 찾기',
        }}
        side={{
          head: '보기',
          active: view,
          onChange: changeView,
          items: [
            { key: 'providers', label: '공급자', count: apiRows.length },
            { key: 'weather-grid', label: '날씨 위치', count: gridCount.places },
            { key: 'stt', label: 'STT', count: sttRows.length },
            ...(diarizeShown ? [{ key: 'diarize', label: '화자 구분', count: diarizeRows.length }] : []),
            { key: 'voice-storage', label: '음성 저장', count: storageCount.companies },
            { key: 'glossary', label: '용어집', count: glossaryCount.total },
          ],
        }}
        filters={{
          // 용어집은 거르개를 두지 않는다 — 상태(들어감 · 한도 밖 · 꺼짐)는 차례에 매여 있어 걸러 보면 경계가 안 보인다.
          items: isGlossary ? [] : isStorage
            ? [
              { key: 'all', label: '전체', count: storageCount.companies },
              // ⚠ 「찬 곳」 = 녹음이 «지금 멈춰 있는» 회사다 — 곧 참(90%)과 다른 칸이다.
              { key: 'full', label: '찬 곳', count: storageCount.full },
            ]
            // 화자 구분도 STT 처럼 거르개를 두지 않는다 — 엔진이 한둘이다.
            : isDiarize
            ? []
            : isStt
            // ⚠ STT 는 거르개를 두지 않는다 — 엔진이 한둘이라 거를 것이 없다(빈 거르개는 헛것이다).
            ? []
            : isGrid
            ? [
              { key: 'all', label: '전체', count: gridCount.places },
              // ⚠ 「고칠 곳」 = 위치를 못 셈한 곳(주소 없음 + 못 푼 주소). 키 문제와는 다른 칸이다.
              { key: 'bad', label: '고칠 곳', count: gridCount.bad },
            ]
            : [
              { key: 'all', label: '전체', count: list.length },
              { key: 'weather', label: '날씨', count: list.filter((p) => p.category === 'weather').length },
              { key: 'address', label: '주소', count: list.filter((p) => p.category === 'address').length },
            ],
          active: filter,
          onChange: (k) => setFilter(k as 'all' | ExternalApiCategory | 'bad' | 'full'),
        }}
        // ⛔ 날씨 위치 보기에는 등록 단추를 두지 않는다 — 곳은 여기서 만드는 것이 아니다(8443 의 사업장 화면).
        // ⛔ STT 는 «서버가 부를 줄 알 때만» 등록을 연다 — 그전에는 눌러도 서버가 거절한다.
        // ⛔ 음성 저장에는 등록할 것이 없다 — 회사가 생기면 저절로 는다.
        toolsExtra={isGlossary
          ? <button type="button" className="xa-btn" onClick={() => setGlossaryAsk('bulk')}>여러 개 넣기</button>
          : undefined}
        add={isGlossary ? { label: '용어 추가', onClick: () => setGlossaryAsk('add') } : isGrid || isStorage
          ? undefined
          // ⭐ 화자 구분도 «서버가 부를 줄 알 때만» 등록을 연다.
          : isDiarize
            ? (diarizeReady ? { label: '엔진 등록', onClick: () => setAddingDiarize(true) } : undefined)
          : isStt
            ? (sttReady ? { label: '엔진 등록', onClick: () => setAddingStt(true) } : undefined)
            : { label: '공급자 등록', onClick: () => setAdding(true) }}
        notice={
          <>
            {/* ⛔ 키가 없으면 날씨가 통째로 안 돈다 — 관리자가 스스로 알아야 한다. */}
            {!loading && !weatherLive && !isStt && !isStorage && !isDiarize && !isGlossary && (
              <div className="xa-band xa-band--bad" role="alert">
                <AlertTriangle size={18} aria-hidden />
                <span className="xa-band__t">
                  <b>날씨가 켜져 있지 않습니다</b>
                  <span>기상청 키를 등록해야 대시보드와 앱의 날씨가 나옵니다 · 주소 검색도 같은 자리에서 켭니다.</span>
                </span>
                <button type="button" className="xa-btn xa-btn--primary" onClick={() => setAdding(true)}>공급자 등록</button>
              </div>
            )}
            {error && <div className="xa-band xa-band--bad" role="alert"><AlertTriangle size={18} aria-hidden /><span className="xa-band__t"><b>{error}</b></span></div>}
            {notice && <div className="xa-band xa-band--info" role="status"><Globe size={18} aria-hidden /><span className="xa-band__t"><b>{notice}</b></span></div>}
          </>
        }
      >
        {isGlossary ? (
          <GlossaryPanel q={q} ask={glossaryAsk} onAskDone={onGlossaryAskDone} onCount={onGlossaryCount} />
        ) : isGrid ? (
          <WeatherGridPanel q={q} onlyBad={filter === 'bad'} onCount={onGridCount} />
        ) : isDiarize ? (
          <DiarizePanel
            providers={shownDiarize}
            total={diarizeRows.length}
            adapters={adapters}
            loading={loading}
            busyKey={busyKey}
            onTest={(p) => void runTest(p)}
            onOpen={setEditingDiarize}
          />
        ) : isStorage ? (
          <VoiceStoragePanel q={q} onlyFull={filter === 'full'} onCount={onStorageCount} />
        ) : isStt ? (
          <SttPanel
            providers={shownStt}
            adapters={adapters}
            loading={loading}
            busyKey={busyKey}
            onTest={(p) => void runTest(p)}
            onOpen={setEditingStt}
            onAdd={() => setAddingStt(true)}
          />
        ) : (
        <ConsoleListTable
          rows={shown}
          rowKey={(p) => p.provider_key}
          onRowClick={(p) => setEditing(p)}
          empty={loading ? '불러오는 중…' : '등록된 공급자가 없습니다 · 「공급자 등록」으로 시작합니다'}
          columns={[
            {
              key: 'provider',
              header: '공급자',
              render: (p) => (
                <span className="xa-cell">
                  <b>{p.label}</b>
                  <span>{CATEGORY_LABEL[p.category]} · {p.provider_key}{p.adapter_available ? '' : ' · 부를 수 없음'}</span>
                </span>
              ),
            },
            { key: 'key', header: '키', render: keyCell },
            {
              key: 'state',
              header: '상태',
              render: (p) => (p.enabled
                ? <span className="xa-badge xa-badge--ok">켜짐</span>
                : <span className="xa-badge xa-badge--muted">꺼짐</span>),
            },
            {
              key: 'usage',
              header: '오늘 사용',
              render: (p) => (p.usage_today
                ? (
                  <span className="xa-usage">
                    {p.usage_today.by_kind.map((k) => <UsageRow key={k.kind} row={k} quota={p.daily_quota} />)}
                  </span>
                )
                // ⚠ 「0회」가 아니라 «기록 없음»이다 — 오늘 한 번도 안 불렀다는 뜻.
                : <span className="xa-dim">오늘 기록 없음</span>),
            },
            {
              key: 'probe',
              header: '마지막 시험',
              render: (p) => (p.last_probe_status
                ? (
                  <span className="xa-cell">
                    <span className={`xa-badge ${VERDICT_CLASS[p.last_probe_status] ?? 'xa-badge--muted'}`}>
                      {VERDICT_LABEL[p.last_probe_status] ?? p.last_probe_status}
                    </span>
                    <span>{fmtDate(p.last_probe_at)}</span>
                  </span>
                )
                : <span className="xa-dim">아직 안 함</span>),
            },
          ]}
          rowActions={(p) => (
            <>
              <button
                type="button"
                className="xa-btn"
                disabled={busyKey === p.provider_key || !p.adapter_available}
                onClick={(e) => { e.stopPropagation(); void runTest(p); }}
              >
                {busyKey === p.provider_key ? '시험 중…' : '연결 시험'}
              </button>
              <button type="button" className="xa-btn" onClick={(e) => { e.stopPropagation(); setEditing(p); }}>열기</button>
            </>
          )}
        />
        )}
      </ConsoleListScreen>

      {(addingStt || editingStt) && (
        <SttEditModal
          provider={editingStt}
          adapters={adapters}
          onClose={() => { setAddingStt(false); setEditingStt(null); }}
          onSaved={(msg) => { setAddingStt(false); setEditingStt(null); setNotice(msg); void reload(); }}
          // ⭐ 2026-10-23 — STT 「엔진 종류」에서 화자 구분을 고르면 그 칸으로 옮겨 등록 창을 연다.
          onPickDiarize={diarizeReady ? () => { setAddingStt(false); setEditingStt(null); changeView('diarize'); setAddingDiarize(true); } : undefined}
        />
      )}

      {(addingDiarize || editingDiarize) && (
        <DiarizeEditModal
          provider={editingDiarize}
          adapters={adapters}
          onClose={() => { setAddingDiarize(false); setEditingDiarize(null); }}
          onSaved={(msg) => { setAddingDiarize(false); setEditingDiarize(null); setNotice(msg); void reload(); }}
        />
      )}

      {(adding || editing) && (
        <ExternalApiEditModal
          provider={editing}
          // ⭐ 화자 구분은 제 칸에서만 등록한다 — 공급자 등록에서 고르면 끝점 없이 저장하려다 서버가 거절한다.
          adapters={adapters.filter((a) => a.category !== 'diarize')}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={(msg) => { setAdding(false); setEditing(null); setNotice(msg); void reload(); }}
        />
      )}
    </>
  );
}
